// Photo registry — maps article slugs (or category slugs as fallback) to
// hot-linked Unsplash photos. Each entry carries its own attribution which
// is displayed below the hero per the Unsplash license.
//
// HOW TO ADD A NEW PHOTO:
//   1. Find a non-Unsplash+ photo on https://unsplash.com (search "panama
//      city", "passport stamp", etc.). Photos under plus.unsplash.com are
//      premium and cannot be hot-linked.
//   2. On the photo's page, look at the og:image tag (or right-click → copy
//      image URL). The URL looks like
//        https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?...
//      The piece you need is the chunk after `photo-`:
//        1526304640581-d334cdbbf45e
//   3. Add an entry below using `asset:` for that chunk, plus `author` and
//      `authorUrl` from the photographer credits at top-right of the photo
//      page.
//   4. Commit + push. Site picks it up automatically.

const UNSPLASH_BASE = 'https://images.unsplash.com/photo-';

/** Build a hot-linked Unsplash photo URL with size + format params. */
export function buildUnsplashUrl(asset, { width = 1600, quality = 80 } = {}) {
  return `${UNSPLASH_BASE}${asset}?auto=format&fit=crop&w=${width}&q=${quality}`;
}

const a = (path) => (path?.startsWith('http') ? path : `https://unsplash.com${path}`);

export const PHOTOS = {
  // Per-article overrides — these win over the category fallback.
  byArticleSlug: {
    'ouvrir-compte-bancaire-panama': {
      asset: '1526304640581-d334cdbbf45e',
      alt: 'Billets de un dollar US empilés',
      author: 'Alexander Grey',
      authorUrl: a('/@sharonmccutcheon'),
    },
    'open-bank-account-panama': {
      asset: '1526304640581-d334cdbbf45e',
      alt: 'Stacked one-dollar US banknotes',
      author: 'Alexander Grey',
      authorUrl: a('/@sharonmccutcheon'),
    },
    'abrir-cuenta-bancaria-panama': {
      asset: '1526304640581-d334cdbbf45e',
      alt: 'Billetes de un dólar EE. UU. apilados',
      author: 'Alexander Grey',
      authorUrl: a('/@sharonmccutcheon'),
    },

    'vols-paris-panama-city': {
      asset: '1545132147-d037e6c54cfd',
      alt: "Hublot d'avion donnant sur une mer de nuages",
      author: 'Sasha Freemind',
      authorUrl: a('/@sashafreemind'),
    },
    'cheap-flights-panama-city': {
      asset: '1545132147-d037e6c54cfd',
      alt: 'Plane window overlooking a sea of clouds',
      author: 'Sasha Freemind',
      authorUrl: a('/@sashafreemind'),
    },
    'vuelos-baratos-panama': {
      asset: '1545132147-d037e6c54cfd',
      alt: 'Ventana de avión sobre un mar de nubes',
      author: 'Sasha Freemind',
      authorUrl: a('/@sashafreemind'),
    },

    'assurance-voyage-panama': {
      asset: '1629410484397-a4dcd74088a0',
      alt: "Couloir lumineux d'un hôpital moderne",
      author: 'Gonzalo Kenny',
      authorUrl: a('/@gonzakenny'),
    },
    'travel-insurance-panama': {
      asset: '1629410484397-a4dcd74088a0',
      alt: 'Bright corridor of a modern hospital',
      author: 'Gonzalo Kenny',
      authorUrl: a('/@gonzakenny'),
    },

    'venir-panama-avec-chien': {
      asset: '1559190394-df5a28aab5c5',
      alt: "Golden retriever assis dans le coffre d'une voiture",
      author: 'Ja San Miguel',
      authorUrl: a('/@jasanmiguel'),
    },
    'bring-dog-to-panama': {
      asset: '1559190394-df5a28aab5c5',
      alt: 'Golden retriever sitting in a car trunk',
      author: 'Ja San Miguel',
      authorUrl: a('/@jasanmiguel'),
    },
    'llevar-perro-panama': {
      asset: '1559190394-df5a28aab5c5',
      alt: 'Golden retriever sentado en el maletero de un coche',
      author: 'Ja San Miguel',
      authorUrl: a('/@jasanmiguel'),
    },

    'friendly-nations-visa-panama': {
      asset: '1654163600175-efc47ce20b29',
      alt: "Page de passeport avec tampons d'immigration",
      author: 'Global Residence Index',
      authorUrl: a('/@globalresidenceindex'),
    },

    'apprendre-espagnol-panama': {
      asset: '1505489501961-3f42fecbe65a',
      alt: "Page d'un livre ouvert",
      author: 'Marta Baena Simino',
      authorUrl: a('/@martasimino'),
    },

    'cout-vie-panama-city': {
      // VERIFIED Panama City skyline (Avenida Balboa towers + marina at sunset)
      asset: '1524544187526-2b2855b65f16',
      alt: 'Skyline Panama City : tours d\'Avenida Balboa et marina au coucher de soleil',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },

    'pensionado-visa-panama': {
      asset: '1581553672347-95d9444c0d2c',
      alt: "Carte d'identité panaméenne posée sur fond clair",
      author: 'Kit',
      authorUrl: a('/@kit'),
    },

    'acheter-appartement-costa-del-este': {
      // VERIFIED Panama City skyline showing tours d'Avenida Balboa (real
      // Costa del Este photos pending verification — using city-wide skyline
      // for now to keep the visual honest)
      asset: '1524544187526-2b2855b65f16',
      alt: 'Skyline de Panama City avec tours résidentielles le long de la baie',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },
    'buy-apartment-costa-del-este': {
      asset: '1524544187526-2b2855b65f16',
      alt: 'Panama City skyline with residential towers along the bay',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },
    'comprar-apartamento-costa-del-este': {
      asset: '1524544187526-2b2855b65f16',
      alt: 'Skyline de Ciudad de Panamá con torres residenciales a lo largo de la bahía',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },

    'cost-of-living-panama-city': {
      asset: '1524544187526-2b2855b65f16',
      alt: 'Panama City skyline at sunset (Avenida Balboa towers + marina)',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },
    'costo-vida-panama-city': {
      asset: '1524544187526-2b2855b65f16',
      alt: 'Skyline de Ciudad de Panamá al atardecer (torres de Avenida Balboa)',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },

    'comprendre-fiscalite-territoriale-panama': {
      asset: '1454165804606-c3d57bc86b40',
      alt: "Bureau d'un comptable avec calculatrice et documents fiscaux",
      author: 'Christin Hume',
      authorUrl: a('/@christinhume'),
    },

    'panama-pour-qui': {
      // VERIFIED Casco Viejo: pastel colonial facades with wrought-iron balconies
      asset: '1656781313303-51379444b553',
      alt: 'Façades coloniales pastel avec balcons en fer forgé, Casco Viejo Panama',
      author: 'Kaori Hirakata',
      authorUrl: a('/@kaorihirakata'),
    },
    'who-is-panama-for': {
      asset: '1656781313303-51379444b553',
      alt: 'Pastel colonial facades with wrought-iron balconies, Casco Viejo Panama',
      author: 'Kaori Hirakata',
      authorUrl: a('/@kaorihirakata'),
    },
    'panama-para-quien': {
      asset: '1656781313303-51379444b553',
      alt: 'Fachadas coloniales pastel con balcones de hierro forjado, Casco Viejo Panamá',
      author: 'Kaori Hirakata',
      authorUrl: a('/@kaorihirakata'),
    },

    'understanding-territorial-taxation-panama': {
      asset: '1454165804606-c3d57bc86b40',
      alt: "Accountant's desk with calculator and tax documents",
      author: 'Christin Hume',
      authorUrl: a('/@christinhume'),
    },
    'entender-fiscalidad-territorial-panama': {
      asset: '1454165804606-c3d57bc86b40',
      alt: 'Oficina de un contador panameño con calculadora y documentos fiscales',
      author: 'Christin Hume',
      authorUrl: a('/@christinhume'),
    },

    'arrivee-panama-72h': {
      asset: '1436491865332-7a61a109cc05',
      alt: "Voyageur tirant sa valise à l'arrivée à l'aéroport de Tocumen, Panama City",
      author: 'Anete Lūsiņa',
      authorUrl: a('/@anete_lusina'),
    },

    'decouvrir-panama-8-lieux-incontournables': {
      // VERIFIED Panama City skyline (Avenida Balboa towers + marina)
      asset: '1524544187526-2b2855b65f16',
      alt: 'Skyline Panama City : tours d\'Avenida Balboa et marina au coucher de soleil',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },
    'discover-panama-8-must-see-places': {
      asset: '1524544187526-2b2855b65f16',
      alt: 'Panama City skyline: Avenida Balboa towers and marina at sunset',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },
    'descubrir-panama-8-lugares': {
      asset: '1524544187526-2b2855b65f16',
      alt: 'Skyline Ciudad de Panamá: torres de Avenida Balboa y marina al atardecer',
      author: 'Miguel Bruna',
      authorUrl: a('/@miguelbruna'),
    },

    'climat-panama-mois-par-mois': {
      asset: '1518495973542-4542c06a5843',
      alt: 'Ciel tropical avec nuages au-dessus de la jungle panaméenne',
      author: 'Steven Kamenar',
      authorUrl: a('/@skamenar'),
    },

    'gastronomie-panama-12-plats': {
      asset: '1546069901-ba9599a7e63c',
      alt: 'Plat de ceviche frais avec patacones, agrumes et coriandre',
      author: 'Brooke Lark',
      authorUrl: a('/@brookelark'),
    },

    'arrival-panama-72h': {
      asset: '1436491865332-7a61a109cc05',
      alt: 'Traveler with suitcase arriving at Tocumen Airport, Panama City',
      author: 'Anete Lūsiņa',
      authorUrl: a('/@anete_lusina'),
    },
    'llegada-panama-72h': {
      asset: '1436491865332-7a61a109cc05',
      alt: 'Viajero arrastrando su maleta a la llegada al aeropuerto de Tocumen, Ciudad de Panamá',
      author: 'Anete Lūsiņa',
      authorUrl: a('/@anete_lusina'),
    },

    'numero-panama-sim-pas-a-pas': {
      asset: '1567721913486-6585f069b332',
      alt: 'Cartes SIM panaméennes posées sur une table avec un téléphone',
      author: 'Brett Jordan',
      authorUrl: a('/@brett_jordan'),
    },

    'learn-spanish-before-panama': {
      asset: '1505489501961-3f42fecbe65a',
      alt: 'Open book page',
      author: 'Marta Baena Simino',
      authorUrl: a('/@martasimino'),
    },
  },

  // Per-category fallback — used when an article has no specific entry.
  byCategorySlug: {
    'visa-residence': {
      asset: '1581553672347-95d9444c0d2c',
      alt: "Carte d'identité posée sur fond clair",
      author: 'Kit',
      authorUrl: a('/@kit'),
    },
    'banque-argent': {
      asset: '1568581357391-c71a1675ef93',
      alt: 'Liasse de billets de 100 dollars US',
      author: 'Mackenzie Marco',
      authorUrl: a('/@kenziem'),
    },
    'assurance': {
      asset: '1631248055158-edec7a3c072b',
      alt: 'Cabinet médical moderne',
      author: 'Petr Magera',
      authorUrl: a('/@mpetrucho'),
    },
    'animaux-chiens': {
      asset: '1581553738617-14ac063e3ba5',
      alt: 'Chien à côté d’une valise',
      author: 'Kit',
      authorUrl: a('/@kit'),
    },
    'vols': {
      asset: '1436491865332-7a61a109cc05',
      alt: 'Avion de ligne photographié depuis le sol',
      author: 'Ross Parmly',
      authorUrl: a('/@rparmly'),
    },
    'espagnol': {
      asset: '1604342162684-0cb7869cc445',
      alt: 'Lettres en espagnol sortant d’un livre',
      author: 'Héctor J. Rivas',
      authorUrl: a('/@hjrc33'),
    },
    'vie-quotidienne': {
      asset: '1656781313303-51379444b553',
      alt: 'Façades à balcons de Casco Viejo, Panama City',
      author: 'Kaori Hirakata',
      authorUrl: a('/@kcreepers'),
    },
    'immobilier': {
      asset: '1524544187526-2b2855b65f16',
      alt: 'Tours de Panama City face à la mer au coucher du soleil',
      author: 'Miguel Bruna',
      authorUrl: a('/@mbrunacr'),
    },
  },

  // Standalone photos for non-article pages.
  pages: {
    home: {
      asset: '1632505702897-cc41b0ba3b64',
      alt: 'Skyline de Panama City vue depuis la baie',
      author: 'Luis Aleman',
      authorUrl: a('/@luisalemanmx'),
    },
    about: {
      asset: '1719439723764-03078f8272d9',
      alt: 'Voiture jaune devant un bâtiment colonial à Casco Viejo',
      author: 'Rustyness',
      authorUrl: a('/@rustyness'),
    },
    flights: {
      asset: '1436491865332-7a61a109cc05',
      alt: 'Avion de ligne photographié depuis le sol',
      author: 'Ross Parmly',
      authorUrl: a('/@rparmly'),
    },
    starthere: {
      asset: '1561588779-25db6ec67cf1',
      alt: 'Skyline de Panama City sous des nuages',
      author: 'Franklin Canelon',
      authorUrl: a('/@franklincanelon'),
    },
  },
};

/**
 * Resolve a photo for an article. Returns:
 *   - The frontmatter `featuredImage` if it's not the og-default placeholder
 *   - Otherwise the per-article entry from PHOTOS.byArticleSlug
 *   - Otherwise the per-category entry from PHOTOS.byCategorySlug
 *   - Otherwise null (caller should fall back to /covers/<category>.svg)
 *
 * `featuredImageCredit` (frontmatter, free text like "Photo Author Name —
 * CC BY-SA 4.0, via Wikimedia Commons") is parsed into author/source so the
 * existing <PhotoAttribution> shows real credit for hotlinked Wikimedia
 * Commons heroes — required by their CC BY/BY-SA licenses, previously not
 * rendered at all (frontmatter featuredImage always returned author: null).
 */
export function photoForArticle({ slug, category, featuredImage, featuredImageAlt, featuredImageCredit }) {
  if (featuredImage && !/\bog-default\.(png|svg)$/.test(featuredImage)) {
    const credit = featuredImageCredit?.match(/^Photo\s+(.+?)\s+—\s+(.+)$/);
    return {
      url: featuredImage,
      alt: featuredImageAlt ?? '',
      author: credit?.[1] ?? null,
      authorUrl: null,
      source: credit?.[2],
    };
  }
  const entry = PHOTOS.byArticleSlug[slug] ?? PHOTOS.byCategorySlug[category];
  if (!entry?.asset) return null;
  return {
    url: buildUnsplashUrl(entry.asset),
    alt: entry.alt ?? featuredImageAlt ?? '',
    author: entry.author,
    authorUrl: entry.authorUrl,
  };
}

/** Same lookup logic for non-article pages (home, about, …). */
export function photoForPage(pageKey) {
  const entry = PHOTOS.pages[pageKey];
  if (!entry?.asset) return null;
  return {
    url: buildUnsplashUrl(entry.asset),
    alt: entry.alt ?? '',
    author: entry.author,
    authorUrl: entry.authorUrl,
  };
}
